<template>
  <div class="trazabilidad-container">
    <!-- Header Responsive -->
    <v-card class="mb-4">
      <v-card-title class="d-flex flex-column flex-md-row justify-space-between align-center ga-2 ga-md-4">
        <h2 class="text-h4 text-center text-md-left">Sistema de Trazabilidad</h2>
        <div class="d-flex flex-column flex-md-row ga-2 ga-md-4 align-center">
          <!-- Botones Desktop -->
          <v-btn 
            color="primary" 
            prepend-icon="mdi-filter-variant"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="d-none d-sm-flex"
          >
            Filtros Avanzados
          </v-btn>
          <v-btn 
            color="success" 
            prepend-icon="mdi-export"
            @click="exportData"
            class="d-none d-sm-flex"
          >
            Exportar
          </v-btn>
          
          <!-- Botones Mobile (solo iconos) -->
          <v-btn 
            color="primary" 
            icon="mdi-filter-variant"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="d-sm-none"
            size="small"
          ></v-btn>
          <v-btn 
            color="success" 
            icon="mdi-export"
            @click="exportData"
            class="d-sm-none"
            size="small"
          ></v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- Filtros Avanzados -->
    <v-expand-transition>
      <v-card v-show="showAdvancedFilters" class="mb-4">
        <v-card-title>
          <v-icon class="mr-2">mdi-filter</v-icon>
          Filtros de Trazabilidad
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.product_id"
                :items="products"
                item-title="name"
                item-value="id"
                label="Producto"
                prepend-inner-icon="mdi-package-variant"
                clearable
                outlined
                dense
                :loading="loadingProducts"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                    <v-list-item-subtitle>SKU: {{ item.raw.sku }}</v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filters.event_type"
                :items="eventTypes"
                label="Tipo de Evento"
                prepend-inner-icon="mdi-history"
                clearable
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.location_id"
                :items="locations"
                item-title="description"
                item-value="id"
                label="Ubicación"
                prepend-inner-icon="mdi-map-marker"
                clearable
                outlined
                dense
                :loading="loadingLocations"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.description }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.raw.code }}</v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filters.lot_number"
                label="Número de Lote"
                prepend-inner-icon="mdi-tag"
                clearable
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filters.gtin"
                label="GTIN / Código de barras"
                prepend-inner-icon="mdi-barcode"
                hint="Acepta GTIN-8/12/13/14"
                clearable
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="filters.date_from"
                label="Fecha Desde"
                type="date"
                prepend-inner-icon="mdi-calendar-start"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="filters.date_to"
                label="Fecha Hasta"
                type="date"
                prepend-inner-icon="mdi-calendar-end"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="d-flex justify-end">
              <v-btn color="primary" @click="applyFilters" class="mr-2">
                <v-icon left>mdi-magnify</v-icon>
                Buscar
              </v-btn>
              <v-btn color="secondary" @click="clearFilters">
                <v-icon left>mdi-filter-remove</v-icon>
                Limpiar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <!-- Tabla de Eventos de Trazabilidad -->
    <v-card>
      <ResponsiveDataTable
        :items="traceabilityEvents || []"
        :headers="headers || []"
        :loading="loading"
        class="elevation-1"
        :items-per-page="itemsPerPage"
        :items-length="totalEvents"
        :page="currentPage"
        @update:page="currentPage = $event"
        :show-select="false"
        density="default"
        hover
      >
        <template v-slot:no-data>
          <div class="text-center py-4">
            <v-icon icon="mdi-timeline-clock" color="warning" size="large"></v-icon>
            <p class="text-subtitle-1 mt-2">No se encontraron eventos de trazabilidad</p>
          </div>
        </template>

        <template v-slot:item.product="{ item }">
          <div v-if="item.product">
            <div class="font-weight-medium">{{ item.product.name || 'N/A' }}</div>
            <div class="text-caption text-medium-emphasis">SKU: {{ item.product.sku || 'N/A' }}</div>
          </div>
          <div v-else class="text-caption text-medium-emphasis">Producto no disponible</div>
        </template>

        <template v-slot:item.event_type="{ item }">
          <v-chip
            v-if="item && item.event_type"
            :color="getEventTypeColor(item.event_type)"
            text-color="white"
            size="small"
          >
            <v-icon start>{{ getEventTypeIcon(item.event_type) }}</v-icon>
            {{ getEventTypeText(item.event_type) }}
          </v-chip>
        </template>

        <template v-slot:item.location="{ item }">
          <div v-if="item.location">
            <div class="font-weight-medium">{{ item.location.description || 'N/A' }}</div>
            <div class="text-caption text-medium-emphasis">{{ item.location.code || 'N/A' }}</div>
          </div>
          <div v-else class="text-caption text-medium-emphasis">Sin ubicación</div>
        </template>

        <template v-slot:item.quantity_moved="{ item }">
          <v-chip
            :color="item.quantity_moved > 0 ? 'success' : 'info'"
            text-color="white"
            size="small"
          >
            {{ item.quantity_moved || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.user="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" class="mr-2">
              <v-icon>mdi-account</v-icon>
            </v-avatar>
            {{ item.user?.username || 'N/A' }}
          </div>
        </template>

        <template v-slot:item.event_timestamp="{ item }">
          <div>
            <div>{{ formatDate(item.event_timestamp) }}</div>
            <div class="text-caption text-medium-emphasis">{{ formatTime(item.event_timestamp) }}</div>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex gap-1">
            <v-btn
              size="small"
              variant="tonal"
              color="info"
              @click="viewEventDetails(item)"
            >
              <v-icon>mdi-eye</v-icon>
              <v-tooltip activator="parent" location="top">
                Ver Detalles
              </v-tooltip>
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              @click="viewProductHistory(item.product_id)"
              :loading="loadingHistory"
            >
              <v-icon>mdi-timeline</v-icon>
              <v-tooltip activator="parent" location="top">
                Historial del Producto
              </v-tooltip>
            </v-btn>
          </div>
        </template>
      </ResponsiveDataTable>
    </v-card>

    <!-- Modal de Detalles del Evento -->
    <v-dialog v-model="showEventDetails" max-width="800">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-information" class="me-2"></v-icon>
          Detalles del Evento
        </v-card-title>
        <v-card-text>
          <div v-if="selectedEvent">
            <v-row>
              <v-col cols="12" md="6">
                <v-list>
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon :icon="getEventTypeIcon(selectedEvent.event_type)" :color="getEventTypeColor(selectedEvent.event_type)"></v-icon>
                    </template>
                    <v-list-item-title>Tipo de Evento</v-list-item-title>
                    <v-list-item-subtitle>{{ getEventTypeText(selectedEvent.event_type) }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-package-variant"></v-icon>
                    </template>
                    <v-list-item-title>Producto</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.product?.name }} ({{ selectedEvent.product?.sku }})</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedEvent.location">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-map-marker"></v-icon>
                    </template>
                    <v-list-item-title>Ubicación</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.location.description }} ({{ selectedEvent.location.code }})</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-account"></v-icon>
                    </template>
                    <v-list-item-title>Usuario</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.user?.username }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-list>
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-clock"></v-icon>
                    </template>
                    <v-list-item-title>Fecha y Hora</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDateTime(selectedEvent.event_timestamp) }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedEvent.quantity_moved">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-counter"></v-icon>
                    </template>
                    <v-list-item-title>Cantidad</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.quantity_moved }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedEvent.lot_number">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-tag"></v-icon>
                    </template>
                    <v-list-item-title>Lote</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.lot_number }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedEvent.operation_reference">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-file-document"></v-icon>
                    </template>
                    <v-list-item-title>Referencia</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEvent.operation_reference }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedEvent.event_description">
              <h6 class="text-h6 mb-2">Descripción</h6>
              <p>{{ selectedEvent.event_description }}</p>
            </div>

            <div v-if="selectedEvent.notes" class="mt-4">
              <h6 class="text-h6 mb-2">Notas</h6>
              <p>{{ selectedEvent.notes }}</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showEventDetails = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Historial del Producto -->
    <v-dialog v-model="showProductHistory" max-width="1000">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-timeline" class="me-2"></v-icon>
          Historial Completo del Producto
        </v-card-title>
        <v-card-text>
          <div v-if="productHistoryData">
            <!-- Información del Producto -->
            <v-card variant="outlined" class="mb-4">
              <v-card-text>
                <div class="d-flex align-center">
                  <v-avatar color="primary" class="me-3">
                    <v-icon>mdi-package-variant</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h6">{{ productHistoryData.product?.name }}</div>
                    <div class="text-caption">SKU: {{ productHistoryData.product?.sku }}</div>
                    <div class="text-caption">Categoría: {{ productHistoryData.product?.category }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="text-right">
                    <div class="text-h6">{{ productHistoryData.statistics?.total_events }} eventos</div>
                    <div class="text-caption">Total registrados</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Timeline de Eventos -->
            <div v-if="loadingHistory" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <p class="mt-4 text-subtitle-1">Cargando historial...</p>
            </div>
            
            <div v-else-if="productHistoryData.history?.length === 0" class="text-center py-8">
              <v-icon icon="mdi-history" size="64" color="grey"></v-icon>
              <p class="text-subtitle-1 mt-4">No hay eventos registrados para este producto</p>
            </div>
            
            <div v-else>
              <v-timeline side="end" class="timeline-custom">
                <v-timeline-item
                  v-for="(event, index) in productHistoryData.history"
                  :key="index"
                  :dot-color="getEventTypeColor(event.event_type)"
                  size="small"
                >
                  <template v-slot:icon>
                    <v-icon :icon="getEventTypeIcon(event.event_type)" size="small"></v-icon>
                  </template>
                  
                  <v-card>
                    <v-card-title class="text-subtitle-1">
                      {{ getEventTypeText(event.event_type) }}
                    </v-card-title>
                    <v-card-text>
                      <p class="mb-2">{{ event.event_description }}</p>
                      <div class="d-flex align-center text-caption text-medium-emphasis">
                        <v-icon icon="mdi-clock" size="small" class="me-1"></v-icon>
                        {{ formatDateTime(event.event_timestamp) }}
                        <v-spacer></v-spacer>
                        <v-icon icon="mdi-account" size="small" class="me-1"></v-icon>
                        {{ event.user?.username }}
                      </div>
                      <div v-if="event.location" class="mt-1 text-caption">
                        <v-icon icon="mdi-map-marker" size="small" class="me-1"></v-icon>
                        {{ event.location.description }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </v-timeline>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showProductHistory = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import api from '@/api'

export default {
  name: 'Trazabilidad',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      // Modales
      showEventDetails: false,
      showProductHistory: false,

      // Datos específicos
      selectedEvent: null,
      productHistoryData: null,
      loadingHistory: false,

      // Carga
      loading: false,
      loadingProducts: false,
      loadingLocations: false,

      // Datos
      traceabilityEvents: [],
      products: [],
      locations: [],

      // Filtros
      showAdvancedFilters: false,
      filters: {
        product_id: null,
        event_type: null,
        location_id: null,
        lot_number: '',
        gtin: '',
        date_from: '',
        date_to: ''
      },

      // Paginación
      currentPage: 1,
      itemsPerPage: 25,
      totalEvents: 0,

      // Configuración de tabla
      headers: [
        { title: 'Producto', key: 'product', sortable: false },
        { title: 'Evento', key: 'event_type', sortable: true },
        { title: 'Ubicación', key: 'location', sortable: false },
        { title: 'Cantidad', key: 'quantity_moved', sortable: true },
        { title: 'Usuario', key: 'user', sortable: false },
        { title: 'Fecha/Hora', key: 'event_timestamp', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],

      // Tipos de eventos
      eventTypes: [
        { title: 'Recepción', value: 'RECEPTION' },
        { title: 'Almacenamiento', value: 'STORAGE' },
        { title: 'Movimiento', value: 'TRANSFER' },
        { title: 'Picking', value: 'PICKING' },
        { title: 'Despacho', value: 'DISPATCH' },
        { title: 'Ajuste', value: 'ADJUSTMENT' },
        { title: 'Devolución', value: 'RETURN' },
        { title: 'Control de Calidad', value: 'QUALITY_CHECK' },
        { title: 'Daño', value: 'DAMAGE' },
        { title: 'Vencimiento', value: 'EXPIRY' }
      ]
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser'])
  },

  async mounted() {
    await this.loadInitialData()
  },

  methods: {
    ...mapActions('inventory', ['fetchInventory']),

    async loadInitialData() {
      this.loading = true
      try {
        await Promise.allSettled([
          this.loadTraceabilityEvents(),
          this.loadProducts(),
          this.loadLocations()
        ])
      } catch (error) {
        console.error('Error cargando datos iniciales:', error)
        console.error('Error cargando datos de trazabilidad')
      } finally {
        this.loading = false
      }
    },

    async loadTraceabilityEvents() {
      try {
        const params = {
          page: this.currentPage,
          per_page: this.itemsPerPage,
          ...this.getActiveFilters()
        }

        const response = await api.getTraceabilityEvents(params)
        this.traceabilityEvents = response.data.events || []
        this.totalEvents = response.data.pagination?.total || 0
      } catch (error) {
        console.error('Error cargando eventos de trazabilidad:', error)
        console.error('Error cargando eventos de trazabilidad')
      }
    },

    async loadProducts() {
      this.loadingProducts = true
      try {
        const response = await api.getProducts()
        this.products = response.data || []
      } catch (error) {
        console.error('Error cargando productos:', error)
      } finally {
        this.loadingProducts = false
      }
    },

    async loadLocations() {
      this.loadingLocations = true
      try {
        const response = await api.getLocations()
        this.locations = response.data || []
      } catch (error) {
        console.error('Error cargando ubicaciones:', error)
      } finally {
        this.loadingLocations = false
      }
    },

    async viewProductHistory(productId) {
      this.loadingHistory = true
      this.showProductHistory = true
      
      try {
        const response = await api.getProductHistory(productId)
        this.productHistoryData = response.data
      } catch (error) {
        console.error('Error cargando historial del producto:', error)
        console.error('Error cargando historial del producto')
      } finally {
        this.loadingHistory = false
      }
    },

    viewEventDetails(event) {
      this.selectedEvent = event
      this.showEventDetails = true
    },

    async applyFilters() {
      this.currentPage = 1
      await this.loadTraceabilityEvents()
      this.showAdvancedFilters = false
    },

    clearFilters() {
      this.filters = {
        product_id: null,
        event_type: null,
        location_id: null,
        lot_number: '',
        gtin: '',
        date_from: '',
        date_to: ''
      }
      this.applyFilters()
    },

    getActiveFilters() {
      const activeFilters = {}
      
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] !== null && this.filters[key] !== '') {
          activeFilters[key] = this.filters[key]
        }
      })
      
      return activeFilters
    },

    getEventTypeColor(eventType) {
      const colors = {
        'RECEPTION': 'success',
        'STORAGE': 'info',
        'TRANSFER': 'warning',
        'PICKING': 'primary',
        'DISPATCH': 'purple',
        'ADJUSTMENT': 'orange',
        'RETURN': 'red',
        'QUALITY_CHECK': 'blue',
        'DAMAGE': 'red-darken-2',
        'EXPIRY': 'grey'
      }
      return colors[eventType] || 'grey'
    },

    getEventTypeIcon(eventType) {
      const icons = {
        'RECEPTION': 'mdi-truck-delivery',
        'STORAGE': 'mdi-warehouse',
        'TRANSFER': 'mdi-swap-horizontal',
        'PICKING': 'mdi-hand-back-left',
        'DISPATCH': 'mdi-send',
        'ADJUSTMENT': 'mdi-pencil',
        'RETURN': 'mdi-keyboard-return',
        'QUALITY_CHECK': 'mdi-check-circle',
        'DAMAGE': 'mdi-alert-circle',
        'EXPIRY': 'mdi-clock-alert'
      }
      return icons[eventType] || 'mdi-help'
    },

    getEventTypeText(eventType) {
      const texts = {
        'RECEPTION': 'Recepción',
        'STORAGE': 'Almacenamiento',
        'TRANSFER': 'Movimiento',
        'PICKING': 'Picking',
        'DISPATCH': 'Despacho',
        'ADJUSTMENT': 'Ajuste',
        'RETURN': 'Devolución',
        'QUALITY_CHECK': 'Control de Calidad',
        'DAMAGE': 'Daño',
        'EXPIRY': 'Vencimiento'
      }
      return texts[eventType] || eventType
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('es-ES')
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('es-ES')
    },

    async exportData() {
      try {
        console.log('Preparando exportación...')
        
        const params = { ...this.getActiveFilters(), per_page: 1000 }
        const response = await api.getTraceabilityEvents(params)
        
        const events = response.data.events || []
        
        // Preparar datos para CSV
        const csvData = events.map(event => ({
          'Fecha': this.formatDateTime(event.event_timestamp),
          'Producto': event.product?.name || 'N/A',
          'SKU': event.product?.sku || 'N/A',
          'Tipo de Evento': this.getEventTypeText(event.event_type),
          'Descripción': event.event_description || '',
          'Ubicación': event.location?.description || 'N/A',
          'Cantidad': event.quantity_moved || 0,
          'Lote': event.lot_number || '',
          'Usuario': event.user?.username || 'N/A',
          'Referencia': event.operation_reference || '',
          'Notas': event.notes || ''
        }))
        
        // Convertir a CSV y descargar
        this.downloadCSV(csvData, 'trazabilidad_eventos.csv')
        console.log('Datos exportados exitosamente')
        
      } catch (error) {
        console.error('Error exportando datos:', error)
        console.error('Error al exportar datos')
      }
    },

    downloadCSV(data, filename) {
      if (data.length === 0) return
      
      const headers = Object.keys(data[0])
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => 
          `"${(row[header] || '').toString().replace(/"/g, '""')}"`
        ).join(','))
      ].join('\n')
      
      // BOM UTF-8 para que Excel/Sheets muestre bien las tildes
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },

  watch: {
    currentPage() {
      this.loadTraceabilityEvents()
    },

    showEventDetails(newVal) {
      if (newVal) {
        // Lógica para cuando se abre el modal de detalles
      }
    }
  }
}
</script>

<style scoped>
.trazabilidad-container {
  padding: 20px;
}

.timeline-custom {
  max-height: 500px;
  overflow-y: auto;
}

.chart-container {
  min-height: 300px;
}

@media (max-width: 768px) {
  .trazabilidad-container {
    padding: 10px;
  }
}
</style> 